import { cn } from "@/lib/utils";

// =============================================================================
// TripCardSkeleton
//
// Placeholder version of TripCard rendered during the initial schedule
// fetch. Matches the real card's footprint so the layout doesn't jump
// when data arrives — dispatch sees the same shape they're about to
// scan, just greyed out and pulsing. Two variants for the two view
// modes:
//
//   detailed → mirrors the full card (header line, identity block,
//              schedule lines, address lines, action stub on the right).
//   compact  → mirrors the single-row dense layout.
//
// Block shapes are inline divs with `animate-pulse` for the standard
// "loading shimmer." No content, no a11y noise — the wrapper carries
// aria-hidden so screen-reader users hear the real "Loading…" status
// announced once by BookedTripsView, not 4× by these placeholders.
// =============================================================================

function Bar({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-md bg-foreground/[0.06]",
        className,
      )}
    />
  );
}

export function TripCardSkeleton({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <article
        aria-hidden="true"
        className="flex animate-pulse items-center gap-3 rounded-xl border border-border/70 bg-card/60 px-4 py-3"
      >
        <Bar className="h-3 w-20" />
        <Bar className="h-3 flex-1 max-w-[12rem]" />
        <Bar className="hidden h-3 w-12 sm:block" />
        <Bar className="h-4 w-16 rounded-full" />
      </article>
    );
  }
  return (
    <article
      aria-hidden="true"
      className="animate-pulse rounded-2xl border border-border/70 bg-card/60 p-4"
    >
      <div className="mb-2 flex items-center justify-between border-b border-border/60 pb-1.5">
        <Bar className="h-3.5 w-32" />
        <Bar className="h-4 w-20 rounded-full" />
      </div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <Bar className="h-4 w-36" />
          <Bar className="h-2.5 w-24" />
          <Bar className="mt-1 h-3 w-28" />
          <Bar className="mt-2 h-3 w-44" />
          <Bar className="h-3 w-56" />
          <Bar className="h-3 w-52" />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Bar className="h-8 w-28 rounded-lg" />
        </div>
      </div>
    </article>
  );
}
